import React from 'react';
import { Box, Button, Chip, Container, Grid, Stack, Typography } from '@mui/material';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import DataObjectRoundedIcon from '@mui/icons-material/DataObjectRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { alpha } from '@mui/material/styles';
import Seo from '../common/Seo';
import { servicePages } from '../../servicePagesData';

const highlights = [
	{ value: 'Faster delivery', label: 'Clear architecture and execution without unnecessary complexity' },
	{ value: 'Reliable systems', label: 'Automation, observability, and resilient cloud operations' },
	{ value: 'Better UX', label: 'Products shaped around real workflows and business goals' },
];

const capabilityCards = [
	{
		icon: <BoltRoundedIcon fontSize="small" />,
		title: 'Modernization',
		description: 'Upgrade legacy platforms and simplify brittle systems.',
	},
	{
		icon: <DataObjectRoundedIcon fontSize="small" />,
		title: 'Integration',
		description: 'Connect tools, data, and workflows into one dependable layer.',
	},
	{
		icon: <InsightsRoundedIcon fontSize="small" />,
		title: 'Optimization',
		description: 'Improve performance, visibility, and delivery quality.',
	},
];

const Hero = () => (
	<>
		<Seo
			title="DevOps Automation & Custom Software Development for Canadian Teams | BrokenCosmos Labs"
			description="BrokenCosmos Labs delivers DevOps automation, custom software development, AI integrations, and cloud architecture for teams across Toronto, the GTA, and Canada."
			path="/"
			jsonLd={{
				'@context': 'https://schema.org',
				'@graph': [
					{
						'@type': 'WebSite',
						name: 'BrokenCosmos Labs',
						url: 'https://brokencosmos.com',
					},
					{
						'@type': 'Organization',
						name: 'BrokenCosmos Labs',
						legalName: 'Brokencosmos labs Inc.',
						url: 'https://brokencosmos.com',
						areaServed: 'Canada',
					},
				],
			}}
		/>
		<Box
			sx={{
				position: 'relative',
				overflow: 'hidden',
				pt: { xs: 5, md: 7 },
				pb: { xs: 8, md: 10 },
			}}
		>
			<Box
				sx={{
					position: 'absolute',
					inset: 0,
					background: `
						radial-gradient(circle at 16% 18%, rgba(37,99,235,0.14), transparent 28%),
						radial-gradient(circle at 84% 18%, rgba(20,184,166,0.12), transparent 24%)
					`,
					pointerEvents: 'none',
				}}
			/>
			<Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
				<Grid container spacing={{ xs: 5, md: 6 }} alignItems="center">
					<Grid item xs={12} lg={7}>
						<motion.div initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.65 }}>
							<Chip
								label="Professional software systems for ambitious teams"
								sx={{
									mb: 3,
									px: 1.2,
									height: 36,
									borderRadius: '999px',
									backgroundColor: alpha('#0f172a', 0.72),
									border: `1px solid ${alpha('#e2e8f0', 0.1)}`,
									color: 'text.primary',
								}}
							/>
							<Typography
								variant="h1"
								component="h1"
								sx={{
									maxWidth: { xs: '12ch', md: '10ch' },
									mb: 2.5,
								}}
							>
								Modernize the systems your business depends on
							</Typography>
							<Typography
								variant="body1"
								sx={{
									maxWidth: 640,
									mb: 4,
									fontSize: { xs: '1rem', md: '1.1rem' },
								}}
							>
								BrokenCosmos Labs helps companies across Toronto, the GTA, and Canada replace friction with clarity
								through cleaner software experiences, better connected tooling, and cloud infrastructure that scales
								with confidence.
							</Typography>
							<Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 4.5 }}>
								<Button component={Link} to="/contact" variant="contained" size="large" endIcon={<ArrowForwardRoundedIcon />}>
									Start a Project
								</Button>
								<Button component={Link} to="/services" variant="outlined" color="secondary" size="large">
									View Services
								</Button>
							</Stack>
							<Stack
								direction={{ xs: 'column', md: 'row' }}
								spacing={{ xs: 1, md: 2.5 }}
								sx={{ mb: 4.5 }}
							>
								{servicePages.map((service) => (
									<Typography
										key={service.key}
										component={Link}
										to={service.path}
										variant="body2"
										sx={{
											color: '#93c5fd',
											textDecoration: 'none',
											'&:hover': { color: '#bfdbfe' },
										}}
									>
										{service.kicker}
									</Typography>
								))}
							</Stack>
						</motion.div>

						<Grid container spacing={2}>
							{highlights.map((item, index) => (
								<Grid item xs={12} md={4} key={item.value}>
									<motion.div
										initial={{ opacity: 0, y: 20 }}
										animate={{ opacity: 1, y: 0 }}
										transition={{ duration: 0.45, delay: 0.18 + index * 0.12 }}
									>
										<Box
											sx={{
												height: '100%',
												p: 2.4,
												borderRadius: '20px',
												backgroundColor: alpha('#0f172a', 0.58),
												border: `1px solid ${alpha('#e2e8f0', 0.07)}`,
											}}
										>
											<Typography variant="h6" sx={{ mb: 1 }}>
												{item.value}
											</Typography>
											<Typography variant="body2">{item.label}</Typography>
										</Box>
									</motion.div>
								</Grid>
							))}
						</Grid>
					</Grid>

					<Grid item xs={12} lg={5}>
						<motion.div initial={{ opacity: 0, x: 18 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.65, delay: 0.2 }}>
							<Box
								sx={{
									p: { xs: 3, md: 3.5 },
									borderRadius: '36px',
									background: 'linear-gradient(180deg, rgba(15,23,42,0.78) 0%, rgba(15,27,45,0.62) 100%)',
									border: `1px solid ${alpha('#e2e8f0', 0.08)}`,
									boxShadow: '0 24px 60px rgba(2, 6, 23, 0.26)',
								}}
							>
								<Box
									sx={{
										p: 2.5,
										borderRadius: '28px',
										background: 'linear-gradient(135deg, rgba(37,99,235,0.18) 0%, rgba(37,99,235,0.05) 100%)',
										border: `1px solid ${alpha('#60a5fa', 0.18)}`,
										mb: 2,
									}}
								>
									<Typography variant="overline" sx={{ color: '#bfdbfe', letterSpacing: '0.08em' }}>
										What we deliver
									</Typography>
									<Typography variant="h4" sx={{ mt: 1, mb: 1.2, maxWidth: '14ch' }}>
										A more capable digital foundation
									</Typography>
									<Typography variant="body2">
										From architecture to deployment, we focus on upgrades clients notice and teams can sustain.
									</Typography>
								</Box>

								<Stack spacing={1.5}>
									{capabilityCards.map((card) => (
										<Box
											key={card.title}
											sx={{
												display: 'flex',
												alignItems: 'flex-start',
												gap: 1.8,
												p: 2.2,
												borderRadius: '24px',
												backgroundColor: alpha('#08101d', 0.52),
												border: `1px solid ${alpha('#e2e8f0', 0.06)}`,
											}}
										>
											<Box
												sx={{
													flexShrink: 0,
													width: 40,
													height: 40,
													borderRadius: '14px',
													display: 'grid',
													placeItems: 'center',
													backgroundColor: alpha('#14b8a6', 0.12),
													color: '#99f6e4',
												}}
											>
												{card.icon}
											</Box>
											<Box>
												<Typography variant="h6" sx={{ mb: 0.6 }}>
													{card.title}
												</Typography>
												<Typography variant="body2">{card.description}</Typography>
											</Box>
										</Box>
									))}
								</Stack>
							</Box>
						</motion.div>
					</Grid>
				</Grid>
			</Container>
		</Box>
	</>
);

export default Hero;
